import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';

export default function RealEstateMarketGrowth() {
  const data = [
    { year: '2024', value: 300, label: '$300B', isCurrent: true },
    { year: '2030', value: 650, label: '$650B', estimated: true },
    { year: '2034', value: 1370, label: '$1.37T', estimated: true },
    { year: '2040', value: 3000, label: '$3T', estimated: true },
    { year: '2047', value: 5170, label: '$5.17T', estimated: true }
  ];

  const maxValue = 5200;

  return (
    <div className="py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A]"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center space-x-2 px-6 py-3 bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-full mb-8">
            <TrendingUp className="w-4 h-4 text-[#4ADE80]" />
            <span className="text-sm text-[#4ADE80] tracking-wide">EXPONENTIAL GROWTH</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-7xl font-light mb-6 leading-tight tracking-tight">
            <span className="block text-white/40 mb-2">THE INDIAN REAL ESTATE</span>
            <span className="block">MARKET BOOM.</span>
          </h2>
          <p className="text-xl text-white/50 max-w-3xl mx-auto leading-relaxed">
            a $300 billion market today, projected to explode to $5.17 trillion by 2047. 
            <span className="block mt-2 text-[#4ADE80]">the opportunity is massive. the time is now.</span>
          </p>
        </motion.div>

        {/* Chart */}
        <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-8 md:p-12">
          <div className="relative h-96 mb-12">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-white/40 pr-6">
              <span>$5T</span>
              <span>$4T</span>
              <span>$3T</span>
              <span>$2T</span>
              <span>$1T</span>
              <span>$0</span>
            </div>

            {/* Chart area */}
            <div className="ml-16 h-full relative">
              {/* Grid lines */}
              {[0, 20, 40, 60, 80, 100].map((y) => (
                <div
                  key={y}
                  className="absolute w-full border-t border-white/5"
                  style={{ top: `${y}%` }}
                />
              ))}

              {/* Bars */}
              <div className="flex items-end justify-between h-full pt-8 gap-4">
                {data.map((item, index) => {
                  const heightPercent = (item.value / maxValue) * 100;

                  return (
                    <div key={index} className="flex-1 flex flex-col items-center h-full">
                      {/* Bar container - takes full height for percentage calc */}
                      <div className="flex-1 w-full flex items-end justify-center">
                        <motion.div
                          initial={{ scaleY: 0, opacity: 0 }}
                          whileInView={{ scaleY: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: index * 0.1, ease: "easeOut" }}
                          className={`w-full max-w-[120px] rounded-t-lg relative group cursor-pointer origin-bottom ${
                            item.isCurrent 
                              ? 'bg-gradient-to-t from-[#4ADE80] to-[#4ADE80]/60' 
                              : 'bg-gradient-to-t from-white/20 to-white/10'
                          }`}
                          style={{ height: `${heightPercent}%` }}
                        >
                          {/* Tooltip on hover */}
                          <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className="bg-[#111111] border border-white/20 rounded-lg px-4 py-2 whitespace-nowrap">
                              <p className="text-xs text-white/60 mb-1">{item.year}</p>
                              <p className={`text-lg font-mono ${
                                item.isCurrent ? 'text-[#4ADE80]' : 'text-white'
                              }`}>
                                {item.label}
                              </p>
                            </div>
                          </div>

                          {/* Current indicator */}
                          {item.isCurrent && (
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                              <div className="px-3 py-1 bg-[#4ADE80] text-black text-xs font-medium rounded-full">
                                TODAY
                              </div>
                            </div>
                          )}
                        </motion.div>
                      </div>

                      {/* X-axis label */}
                      <div className="mt-4 text-center">
                        <p className="text-sm font-mono text-white/80 mb-1">{item.year}</p>
                        <p className={`text-xs ${
                          item.isCurrent ? 'text-[#4ADE80]' : 'text-white/40'
                        }`}>
                          {item.label}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-xl p-6"
            >
              <p className="text-3xl font-mono text-[#4ADE80] mb-2">17x</p>
              <p className="text-sm text-white/60">Expected market growth by 2047</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="bg-white/5 border border-white/10 rounded-xl p-6"
            >
              <p className="text-3xl font-mono text-white mb-2">$1.37T</p>
              <p className="text-sm text-white/60">Market size by 2034 (4.5x growth)</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="bg-white/5 border border-white/10 rounded-xl p-6"
            >
              <p className="text-3xl font-mono text-white mb-2">$5.17T</p>
              <p className="text-sm text-white/60">Market size by 2047 (17x growth)</p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}